import styled from "styled-components";
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  Layout,
  List,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { COLORS } from "../../../utils/colors";
import { tablePaginationCss } from "../../components/tablePaginationStyles";
import type { AdminUserStatus } from "./types";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const PageLayout = styled(Layout)`
  && {
    height: 100vh;
    background: linear-gradient(
      180deg,
      ${COLORS.pageGradientStart} 0%,
      ${COLORS.pageGradientEnd} 100%
    );
    font-family: "Roboto", sans-serif;
  }
`;

export const InnerLayout = styled(Layout)`
  && {
    background: transparent;
    height: 100vh;
    overflow-y: auto;
  }
`;

export const PageContent = styled(Content)`
  && {
    padding: 8px 28px 28px;
  }

  @media (max-width: 575px) {
    && {
      padding: 8px 16px 16px;
    }
  }
`;

export const WelcomeRow = styled(Flex)`
  && {
    margin-bottom: 20px;
  }
`;

export const WelcomeBlock = styled(Flex)``;

export const WelcomeTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 28px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 36px !important;
  }

  @media (max-width: 575px) {
    && {
      font-size: 22px !important;
      line-height: 28px !important;
    }
  }
`;

export const WelcomeSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const WelcomeControls = styled(Flex)`
  @media (max-width: 991px) {
    && {
      width: 100%;
      flex-wrap: wrap;
    }
  }
`;

export const DateRangePill = styled(RangePicker)`
  &&.ant-picker {
    height: 38px;
    border-radius: 10px !important;
    padding: 0 18px;
    border: 1px solid ${COLORS.border} !important;
    background: ${COLORS.surface};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    box-shadow: none !important;
    cursor: pointer;
    min-width: 260px;
    overflow: hidden;
  }

  &&.ant-picker .ant-picker-input {
    border-radius: inherit;
  }

  &&.ant-picker .ant-picker-input > input {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    cursor: pointer;
  }

  &&.ant-picker .ant-picker-input > input::placeholder {
    color: ${COLORS.textMuted};
  }

  &&.ant-picker .ant-picker-prefix {
    color: ${COLORS.textBody};
    margin-inline-end: 10px;
    font-size: 14px;
  }

  &&.ant-picker .ant-picker-range-separator {
    color: ${COLORS.textMuted};
    padding: 0 6px;
  }

  &&.ant-picker .ant-picker-suffix {
    color: ${COLORS.textMuted};
    margin-inline-start: 10px;
    font-size: 14px;
  }

  &&.ant-picker .ant-picker-clear {
    background: ${COLORS.surface};
  }

  &&.ant-picker:hover,
  &&.ant-picker-focused {
    border-color: ${COLORS.border} !important;
    box-shadow: none !important;
    border-radius: 10px !important;
  }

  &&.ant-picker .ant-picker-active-bar {
    display: none;
  }

  @media (max-width: 991px) {
    &&.ant-picker {
      min-width: 0;
      flex: 1 1 60%;
    }
  }
` as unknown as typeof RangePicker;

export const DatePopupWrap = styled.div`
  font-family: "Roboto", sans-serif;

  .ant-picker-panel-container {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
    background: ${COLORS.primary};
  }

  .ant-picker-today-btn {
    color: ${COLORS.primary};
  }
`;

export const UsersCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 14px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 16px 18px;
  }
`;

export const UsersHeader = styled(Flex)`
  && {
    margin-bottom: 14px;
  }

  @media (max-width: 991px) {
    && {
      flex-direction: column;
      align-items: stretch;
    }
  }
`;

export const UsersTitleWrap = styled(Flex)``;

export const UsersTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 16px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
  }
`;

export const UsersSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const UsersHeaderControls = styled(Flex)`
  @media (max-width: 991px) {
    && {
      width: 100%;
      flex-wrap: wrap;
    }
  }
`;

export const SearchInput = styled(Input)`
  && {
    height: 36px;
    border-radius: 10px;
    border: 1px solid ${COLORS.border};
    padding: 0 14px;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    width: 220px;
    background: ${COLORS.surface};

    .ant-input {
      font-size: 12px;
      background: transparent;
    }

    .ant-input-prefix {
      margin-inline-end: 10px;
    }

    .ant-input-prefix .anticon {
      color: ${COLORS.textSubtle};
      font-size: 14px;
    }

    &:hover,
    &:focus,
    &:focus-within {
      border-color: ${COLORS.primary};
      box-shadow: none;
    }
  }

  @media (max-width: 991px) {
    && {
      width: 100%;
    }
  }
`;

export const AddUserButton = styled(Button)`
  && {
    height: 36px;
    padding: 0 18px;
    border-radius: 10px;
    background: ${COLORS.primary};
    border: 1px solid ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus,
    &:active {
      background: ${COLORS.primary} !important;
      border-color: ${COLORS.primary} !important;
      color: ${COLORS.textOnPrimary} !important;
      opacity: 0.92;
    }

    &:disabled,
    &:disabled:hover {
      background: ${COLORS.surfaceMuted} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textSubtle} !important;
      cursor: not-allowed;
      opacity: 1;
    }
  }
`;

export const ShowEntriesWrap = styled(Flex)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    margin-left: 4px;
  }
`;

export const EntriesFooter = styled(Flex)`
  && {
    position: absolute;
    right: 8%;
    bottom: 22px;
    height: 32px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
  }
`;

export const ShowEntriesSelect = styled(Select)`
  && {
    width: 58px;
  }

  && .ant-select-selector {
    height: 28px !important;
    border-radius: 6px !important;
    padding: 0 8px 0 10px !important;
    border: none !important;
    background: ${COLORS.entriesbg} !important;
    box-shadow: none !important;
  }

  && .ant-select-selection-item {
    line-height: 26px !important;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.successText};
    padding-inline-end: 14px !important;
  }

  && .ant-select-arrow {
    color: ${COLORS.successText};
    inset-inline-end: 8px;
    font-size: 10px;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    background: ${COLORS.entriesbg} !important;
    box-shadow: none !important;
  }
`;

export const DesktopTableWrap = styled.div`
  width: 100%;
  && .ant-table-placeholder {
    min-height: 220px;
  }

  && .ant-table-pagination.ant-pagination {
    display: flex;
    justify-content: center;
    flex: 1;
    margin-top: 14px !important;
    margin-bottom: 6px !important;
  }
`;

export const StyledTable = styled(Table)`
  && .ant-table {
    background: transparent;
    font-family: "Roboto", sans-serif;
  }

  && .ant-table-content > table,
  && .ant-table-body > table,
  && .ant-table-header > table {
    table-layout: fixed !important;
    width: 100% !important;
  }

  && .ant-table-header {
    background: transparent;
  }

  && .ant-table-thead > tr > th {
    background: transparent;
    color: ${COLORS.textMuted};
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    border-bottom: 1px solid ${COLORS.borderSoft};
    padding: 10px 16px;
  }

  && .ant-table-thead > tr > th::before {
    display: none !important;
  }

  && .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f1f3f6;
    padding: 14px 16px;
    font-size: 12px;
    color: ${COLORS.textBody};
    transition: background 120ms ease;
  }

  && .ant-table-tbody > tr:hover > td {
    background: ${COLORS.surfaceHover};
  }

  && .ant-table-body {
    scrollbar-width: thin;
    scrollbar-color: ${COLORS.border} transparent;
  }

  && .ant-table-body::-webkit-scrollbar {
    width: 6px;
  }

  && .ant-table-body::-webkit-scrollbar-track {
    background: transparent;
  }

  && .ant-table-body::-webkit-scrollbar-thumb {
    background: ${COLORS.border};
    border-radius: 999px;
  }

  && .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.textSubtle};
  }

  ${tablePaginationCss}
`;

export const UserNameText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${COLORS.textBody};
  }
`;

export const UserCellText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const StatusPill = styled(Tag)<{ $status: AdminUserStatus }>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 92px;
    padding: 3px 0;
    border-radius: 999px;
    margin: 0;
    border: none;
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    background: ${({ $status }) =>
      $status === "approved"
        ? COLORS.authorizedBg
        : $status === "pending"
          ? COLORS.pendingBg
          : COLORS.unsuccessBg};
    color: ${({ $status }) =>
      $status === "approved"
        ? COLORS.authorizedText
        : $status === "pending"
          ? COLORS.pendingText
          : COLORS.unsuccessText};
  }
`;

export const SelectActionButton = styled(Button)`
  && {
    height: 32px;
    padding: 0 14px;
    border-radius: 16px;
    background: ${COLORS.surface};
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    box-shadow: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    .anticon {
      font-size: 14px;
      color: ${COLORS.textMuted};
    }

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
    }
  }
`;

export const UserList = styled(List)`
  && .ant-list-items {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  && .ant-list-item {
    display: block;
    padding: 0;
    border-block-end: none;
  }

  && .ant-list-pagination {
    margin-top: 16px;
    text-align: center;
  }

  ${tablePaginationCss}
` as unknown as typeof List;

export const UserCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 12px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;

export const UserCardTopRow = styled(Flex)``;

export const UserCardName = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${COLORS.textBody};
  }
`;

export const UserCardEmail = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const UserCardMeta = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const UserCardActions = styled(Flex)`
  && {
    margin-top: 8px;
  }
`;

export const AddUserModalStyled = styled(Modal)`
  && .ant-modal-content {
    padding: 0;
    border-radius: 16px;
    overflow: hidden;
  }

  && .ant-modal-body {
    padding: 0;
  }
` as unknown as typeof Modal;

export const AddUserModalHeader = styled(Flex)`
  && {
    padding: 20px 24px 18px;
  }
`;

export const AddUserModalHeaderIcon = styled(Flex)`
  && {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${COLORS.iconNotificationBg};
    color: ${COLORS.primary};
    flex-shrink: 0;
  }

  && .anticon {
    font-size: 18px;
    color: ${COLORS.primary};
  }
`;

export const AddUserModalTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 18px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 22px !important;
  }
`;

export const AddUserModalSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
    line-height: 16px;
  }
`;

export const AddUserModalClose = styled(Button)`
  && {
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    color: ${COLORS.textSubtle};
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .anticon {
      font-size: 16px;
    }

    &:hover,
    &:focus {
      background: ${COLORS.surfaceMuted} !important;
      color: ${COLORS.textBody} !important;
    }
  }
`;

export const AddUserModalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${COLORS.divider};
`;

export const AddUserModalBody = styled.div`
  padding: 22px 24px 8px;

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: ${COLORS.textBody};
    height: auto;
  }
`;

export const AddUserInput = styled(Input)`
  && {
    height: 44px;
    border-radius: 24px;
    border: 1px solid ${COLORS.border};
    padding: 0 18px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    background: ${COLORS.surface};

    &::placeholder {
      color: ${COLORS.textSubtle};
    }

    &:hover,
    &:focus,
    &:focus-within {
      border-color: ${COLORS.primary};
      box-shadow: none;
    }
  }
`;

export const AddUserSelect = styled(Select)`
  && {
    width: 100%;
    height: 44px;
  }

  && .ant-select-selector {
    height: 44px !important;
    border-radius: 24px !important;
    padding: 0 18px !important;
    border: 1px solid ${COLORS.border} !important;
    background: ${COLORS.surface} !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
  }

  && .ant-select-selection-item,
  && .ant-select-selection-placeholder {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    line-height: 42px !important;
    display: flex;
    align-items: center;
  }

  && .ant-select-selection-item {
    color: ${COLORS.textBody};
  }

  && .ant-select-selection-placeholder {
    color: ${COLORS.textSubtle};
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.primary} !important;
    box-shadow: none !important;
  }
`;

export const AddUserFieldGrid = styled(Flex)`
  && {
    gap: 16px;
  }

  && > * {
    flex: 1 1 0;
    min-width: 0;
  }

  @media (max-width: 575px) {
    && {
      flex-direction: column;
    }
  }
`;

export const AddUserModalFooter = styled(Flex)`
  && {
    padding: 16px 24px 22px;
  }
`;

export const ModalCancelButton = styled(Button)`
  && {
    height: 38px;
    padding: 0 26px;
    border-radius: 8px;
    background: ${COLORS.surface};
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
    }
  }
`;

export const ModalSubmitButton = styled(Button)`
  && {
    height: 38px;
    padding: 0 22px;
    border-radius: 8px;
    background: ${COLORS.primary};
    border: 1px solid ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus,
    &:active {
      background: ${COLORS.primary} !important;
      border-color: ${COLORS.primary} !important;
      color: ${COLORS.textOnPrimary} !important;
      opacity: 0.92;
    }
  }
`;
